import { useState, type FormEvent } from "react";
import { post } from "../../api/client";
import type { ActiveCall } from "../../api/types";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { TextField } from "../../components/ui/Field";
import { EmptyState, ErrorState, InlineError, Loading } from "../../components/ui/misc";
import { toast } from "../../components/ui/toast";
import { errorMessage } from "../../lib/format";
import { ColleaguePicker, useDirectory } from "./Colleagues";

/** Conference calling: add a colleague or an outside number to the live call. */
export function AddPeopleDialog({ call, open, onOpenChange, onAdded }: { call: ActiveCall; open: boolean; onOpenChange: (o: boolean) => void; onAdded: () => void }) {
  const [tab, setTab] = useState<"colleague" | "number">("colleague");
  const directory = useDirectory(open);
  const [busy, setBusy] = useState<string | null>(null);
  const [number, setNumber] = useState("");
  const [error, setError] = useState<unknown>(null);
  // Colleagues already on the call (including ones still ringing) aren't offered again.
  const onCall = (call.participants ?? []).map((p) => p.membershipId).filter((id): id is string => !!id);

  const addTo = async (key: string, body: Record<string, string>, who: string) => {
    setBusy(key); setError(null);
    try {
      await post(`/calls/${call.id}/participants`, body);
      toast.success(`Calling ${who} into the call`);
      onAdded();
      onOpenChange(false);
    } catch (err) {
      if (tab === "colleague") toast.error(errorMessage(err)); else setError(err);
    } finally { setBusy(null); }
  };
  const submitNumber = (e: FormEvent) => { e.preventDefault(); if (number.trim()) void addTo("number", { number: number.trim() }, number.trim()); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add people to this call" size="lg"
      description="They join the call you're on. Everyone hears each other; you can remove people you added at any time.">
      <div role="tablist" aria-label="Who to add" className="mb-3 flex gap-1 rounded-xl bg-white/40 p-1">
        {(["colleague", "number"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => { setTab(t); setError(null); }}
            className={`focus-ring flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition ${tab === t ? "bg-accent-gradient text-white shadow-glow" : "text-muted hover:bg-white/70"}`}>
            {t === "colleague" ? "Colleague" : "Phone number"}
          </button>
        ))}
      </div>
      {tab === "colleague" ? (
        directory.error ? <ErrorState error={directory.error} /> : !directory.data ? <Loading /> : directory.data.items.length === 0 ? <EmptyState title="No colleagues yet" /> : (
          <ColleaguePicker
            directory={directory.data} actionLabel="Add" busyId={busy}
            exclude={onCall}
            onPick={(c) => void addTo(c.membershipId, { membershipId: c.membershipId }, c.name)}
          />
        )
      ) : (
        <form onSubmit={submitNumber} className="space-y-3">
          <TextField label="Phone number" type="tel" inputMode="tel" autoComplete="off" placeholder="+1 201 555 0123" value={number} onChange={(e) => setNumber(e.target.value)} required />
          <p className="text-xs text-subtle">Your calling rules apply (allowed countries, blocked premium numbers). They see your business number.</p>
          <InlineError error={error} />
          <div className="flex justify-end"><Button type="submit" variant="primary" loading={busy === "number"} disabled={!number.trim()}>Add to call</Button></div>
        </form>
      )}
    </Dialog>
  );
}
